import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './OptionScope.css';

function fmtIdx(n) {
  return Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPnl(n) {
  const v = Number(n) || 0;
  const sign = v > 0 ? '+' : '';
  return `${sign}₹${Math.abs(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function fmtStrike(n) {
  return Number(n || 0).toLocaleString('en-IN');
}

function IndexPill({ card }) {
  if (!card) return null;
  return (
    <div className="os-index">
      <span className="os-index-label">{card.label}</span>
      <span className="os-index-price">{fmtIdx(card.price)}</span>
      <span className={`os-index-chg ${card.up ? 'up' : 'down'}`}>
        {card.changePts >= 0 ? '+' : ''}
        {fmtIdx(card.changePts)}
        {' '}
        ({card.changePct >= 0 ? '+' : ''}
        {fmtIdx(card.changePct)}%)
      </span>
    </div>
  );
}

function OiBar({ value, max, tone }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="os-oi-cell">
      <span>{fmtOiLabel(value)}</span>
      <div className="os-oi-track">
        <div className={`os-oi-fill ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function fmtOiLabel(n) {
  const v = Math.abs(Number(n) || 0);
  if (v >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(Math.round(n || 0));
}

function PcrGauge({ pcr }) {
  // Map PCR ~0.6–1.6 onto 0–100% (bearish → bullish)
  const pct = Math.max(4, Math.min(96, ((Number(pcr) || 1) - 0.55) / 1.1 * 100));
  return (
    <div className="os-card os-pcr">
      <h3>Put/Call Ratio (PCR)</h3>
      <div className="os-pcr-value">{Number(pcr || 0).toFixed(2)}</div>
      <div className="os-pcr-gauge">
        <div className="os-pcr-gradient" />
        <div className="os-pcr-marker" style={{ left: `${pct}%` }} />
      </div>
      <div className="os-pcr-labels">
        <span>Bearish</span>
        <span>Neutral</span>
        <span>Bullish</span>
      </div>
    </div>
  );
}

export default function OptionScope({ pollMs = 8000 }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data: payload } = await axios.get('/api/options/scope', { timeout: 20000 });
      if (payload?.ok === false) {
        setError(payload.error || 'OptionScope failed');
        return;
      }
      setData(payload);
      setError(null);
    } catch (e) {
      setError(e.message || 'Cannot load OptionScope');
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, pollMs);
    return () => clearInterval(t);
  }, [refresh, pollMs]);

  const chain = data?.chain;
  const indices = data?.indices;
  const levels = data?.keyLevels;
  const pnl = data?.todayPnl;

  return (
    <div className="os-app">
      <header className="os-header">
        <div className="os-brand">
          <div className="os-logo" aria-hidden>Ω</div>
          <div>
            <h1>OptionScope</h1>
            <p>Live option chain reader</p>
          </div>
        </div>
        <div className="os-indices">
          <IndexPill card={indices?.nifty} />
          <IndexPill card={indices?.bankNifty} />
          <IndexPill card={indices?.vix} />
        </div>
      </header>

      {error && <div className="os-error">{error}</div>}

      <div className="os-grid">
        <section className="os-card os-chain">
          <header className="os-chain-head">
            <h2>
              {chain?.underlying || 'NIFTY'} Option Chain
              <span> · Expiry {chain?.expiryLabel || '—'}</span>
            </h2>
            <span className="os-chain-meta">OI in lots · updated {data?.updatedAt || '—'}</span>
          </header>

          <div className="os-table-wrap">
            <table className="os-table">
              <thead>
                <tr>
                  <th>OI</th>
                  <th>CHG OI</th>
                  <th>IV</th>
                  <th>LTP</th>
                  <th>Δ</th>
                  <th className="os-strike-col">STRIKE</th>
                  <th>Δ</th>
                  <th>LTP</th>
                  <th>IV</th>
                  <th>CHG OI</th>
                  <th>OI</th>
                </tr>
                <tr className="os-side-row">
                  <th colSpan={5} className="os-calls-label">CALLS</th>
                  <th />
                  <th colSpan={5} className="os-puts-label">PUTS</th>
                </tr>
              </thead>
              <tbody>
                {(chain?.rows || []).map((row) => (
                  <tr key={row.strike} className={row.atm ? 'atm' : ''}>
                    <td>
                      <OiBar value={row.call.oi} max={chain.maxCallOi} tone="call" />
                    </td>
                    <td className={row.call.chgOi >= 0 ? 'up' : 'down'}>{row.call.chgOiLabel}</td>
                    <td>{row.call.iv}</td>
                    <td className="os-ltp-call">{row.call.ltp}</td>
                    <td>{row.call.delta}</td>
                    <td className="os-strike-col">
                      {fmtStrike(row.strike)}
                      {row.atm ? <em className="os-atm-tag">ATM</em> : null}
                    </td>
                    <td>{row.put.delta}</td>
                    <td className="os-ltp-put">{row.put.ltp}</td>
                    <td>{row.put.iv}</td>
                    <td className={row.put.chgOi >= 0 ? 'up' : 'down'}>{row.put.chgOiLabel}</td>
                    <td>
                      <OiBar value={row.put.oi} max={chain.maxPutOi} tone="put" />
                    </td>
                  </tr>
                ))}
                {!chain?.rows?.length && (
                  <tr>
                    <td colSpan={11} className="os-empty">Loading live chain…</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="os-side">
          <PcrGauge pcr={data?.pcr} />

          <div className="os-card os-levels">
            <h3>Key Levels</h3>
            <ul>
              <li>
                <span>Max Pain</span>
                <strong>{fmtStrike(levels?.maxPain)}</strong>
              </li>
              <li>
                <span>Support (highest Put OI)</span>
                <strong className="up">{fmtStrike(levels?.support)}</strong>
              </li>
              <li>
                <span>Resistance (highest Call OI)</span>
                <strong className="down">{fmtStrike(levels?.resistance)}</strong>
              </li>
              <li>
                <span>India VIX</span>
                <strong>{fmtIdx(levels?.vix)}</strong>
              </li>
            </ul>
          </div>

          <div className="os-card os-pnl">
            <h3>Today&apos;s P&amp;L</h3>
            <div className={`os-pnl-total ${(pnl?.total || 0) >= 0 ? 'up' : 'down'}`}>
              {fmtPnl(pnl?.total)}
            </div>
            <p className="os-pnl-summary">{pnl?.summary || '0 open positions · 0 closed'}</p>
            <ul className="os-pnl-list">
              {(pnl?.lines || []).map((line) => (
                <li key={line.id}>
                  <span className={`os-side-badge ${line.side === 'CE' ? 'ce' : 'pe'}`}>{line.side}</span>
                  <span className="os-pnl-label">{line.label}</span>
                  <span className={line.pnl >= 0 ? 'up' : 'down'}>{fmtPnl(line.pnl)}</span>
                </li>
              ))}
              {!pnl?.lines?.length && (
                <li className="os-pnl-empty">No option trades yet today — bot arms at 9:15 IST</li>
              )}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
